// ---------------------------------------------------------------------------
// Ports legacy date-time-modal.js's Spanish-language toggle + Region select
// (Kickoff call only) onto Account Setup's per-location PurchaseLinkSlice
// items. Legacy matched on `product.name` (a flat {centers, apps} cart from
// the old order-builder screens); AccountSetup's items instead carry only
// `productCode` (see scheduling-call-rules.js's item-shape doc comment), so
// every legacy name check below is resolved to its equivalent productCode(s).
//
// Legacy source: src/components/appointments/book-appointments.js
// (isUsSpanishOptionAvailable, isRestrictedAccessHIPPASelected,
// isThryvLeadsOrSeoIncluded) + src/components/date-time-modal.js
// (showOrderCallLanguagePrefs, displayRegionSelect, displayOnboardingRegionSelect).
// ---------------------------------------------------------------------------
import {hasStarterOnboardingOptIn} from '../../../../utils/scheduling-call-rules'
import {MC_2_0_SIGPREMIERPLANS} from '../../../../utils/constants'

export const REGION_OPTIONS = [
  {label: 'Coastal', value: 'Coastal'},
  {label: 'Midwest', value: 'Midwest'},
  {label: 'South', value: 'South'},
  {label: 'Inside', value: 'Inside'},
  {label: 'Inbound', value: 'Inbound'},
  {label: 'Partner Channel', value: 'Partner Channel'},
]

// FULSAS-12295: Thryv Starter's onboarding kickoff call uses its own,
// smaller region list (no Partner Channel) — legacy ONBOARDING_REGION_OPTIONS.
export const ONBOARDING_REGION_OPTIONS = [
  {label: 'Coastal', value: 'Coastal'},
  {label: 'Midwest', value: 'Midwest'},
  {label: 'South', value: 'South'},
  {label: 'Inside', value: 'Inside'},
  {label: 'Inbound', value: 'Inbound'},
]

// legacy isMCSelected ('Marketing Center Pro' | 'Marketing Center Plus')
const MC_PRO_PLUS_CODES = ['mc_pro-5-thryv-m2m', 'mc_plus-5-thryv-m2m']
// legacy hasBCSelected ('Business Center Professional' | 'Plus' | 'Unlimited')
const BC_CODES = [
  'bc_pro-5-thryv-m2m',
  'bc_plus-5-thryv-m2m',
  'bc_unlm-5-thryv-m2m',
]
// legacy hasBC6Selected
const BC6_CODE = 'bc-6-thryv-m2m'
// legacy hasCCSelected
const CC_CODES = [
  'cc_pro-5-thryv-m2m',
  'cc_basicpls-5-thryv-m2m',
  'cc_plus-5-thryv-m2m',
]
// legacy isOnlyNewSocialAdsSelected's Social Ads tiers — same codes as
// scheduling-call-rules.js's APPOINTMENT_REQUIRED_CODES.
const SOCIAL_ADS_CODES = [
  'addon-socialadsbronze-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-socialadssilver-5-m2m',
]
// legacy isRestrictedAccessHIPPASelected ('Restricted Access' | 'HIPAA' apps)
const RESTRICTED_HIPAA_CODES = ['addon-5-hipaa', 'addon-5-restricted']
// legacy isThryvLeadsOrSeoIncluded's Thryv Leads half. The SEO half of that
// legacy check excluded every SEO product name that actually exists in the
// current catalog ('AEO & SEO', 'SEO Keywords', 'AEO & SEO PowerBoost'), so
// it has no live equivalent here — omitted rather than guessed at.
const THRYV_LEADS_CODES = [
  'tl-mc-5-thryv-m2m',
  'tl-tax-5-thryv-m2m',
  'tl-5-thryv-m2m',
]

function hasAnyCode(items, codes) {
  return (items ?? []).some(item => codes.includes(item.productCode))
}

// legacy isOnlyNewSocialAdsSelected — true only when the single newly added
// product across the whole cart is a Social Ads tier.
function isOnlyNewSocialAdsSelected(items) {
  const newItems = (items ?? []).filter(item => !item.alreadyIncluded)
  return (
    newItems.length === 1 && SOCIAL_ADS_CODES.includes(newItems[0].productCode)
  )
}

// legacy isUsSpanishOptionAvailable's plan-combo half (country checked
// separately by callers).
function hasUsSpanishPlanCombo(items) {
  const hasSignature = hasAnyCode(items, MC_2_0_SIGPREMIERPLANS)
  const hasBcOrMcOrBc6 =
    hasAnyCode(items, BC_CODES) ||
    hasAnyCode(items, MC_PRO_PLUS_CODES) ||
    hasAnyCode(items, [BC6_CODE])
  const hasCc = hasAnyCode(items, CC_CODES)
  return (
    (hasSignature || (hasBcOrMcOrBc6 && hasCc)) &&
    !isOnlyNewSocialAdsSelected(items)
  )
}

function isRestrictedOrHipaaSelected(items) {
  return hasAnyCode(items, RESTRICTED_HIPAA_CODES)
}

// Exported — skipCallEligibility.js's shouldRestrictPartnerOpsSkip check needs
// the same "does this cart include Thryv Leads?" test legacy's
// isThryvLeadsOrSeoIncluded used, so it reuses this instead of redefining
// THRYV_LEADS_CODES a second time.
export function isThryvLeadsIncluded(items) {
  return hasAnyCode(items, THRYV_LEADS_CODES)
}

// legacy showOrderCallLanguagePrefs's kickoff branch. Callers only invoke
// this for the Kickoff call — Account Setup never shows this control for
// the Thryv Success Services call, matching legacy.
export function isSpanishToggleEligible(location) {
  const country = location?.country || 'US'
  const items = location?.items ?? []
  return (
    country === 'US' &&
    !isRestrictedOrHipaaSelected(items) &&
    !isThryvLeadsIncluded(items) &&
    // FULSAS-14186: Thryv Starter onboarding was never added to the legacy
    // 5.0 plan-combo list, so Starter+Growth-Package carts never qualified
    // for the Spanish toggle even though PT offers Spanish unconditionally
    // for every Kickoff call, including Starter onboarding. Treat Starter
    // onboarding as a second, independent way to qualify.
    (hasUsSpanishPlanCombo(items) || hasStarterOnboardingOptIn(items))
  )
}

// legacy displayRegionSelect — independent of the plan-combo Spanish check,
// gated off once Spanish is chosen and off for Thryv Starter locations
// (those get their own list — see isStarterRegionSelectEligible).
export function isStandardRegionSelectEligible(location, spanish) {
  const country = location?.country || 'US'
  const items = location?.items ?? []
  return (
    country === 'US' &&
    spanish !== true &&
    !isRestrictedOrHipaaSelected(items) &&
    !isThryvLeadsIncluded(items) &&
    !hasStarterOnboardingOptIn(items)
  )
}

// legacy displayOnboardingRegionSelect — Thryv Starter's onboarding kickoff
// call; legacy has no country/HIPAA/Thryv-Leads/Spanish gate on this one
// either, so none is added here.
export function isStarterRegionSelectEligible(location) {
  return hasStarterOnboardingOptIn(location?.items ?? [])
}
